import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { useAdminCheck } from "@/hooks/useAdminCheck";
import { AdminPasswordProtection } from "@/components/admin/AdminPasswordProtection";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import {
  RefreshCw, Users, Database, Handshake, Monitor, Bell, FileText, UserPlus,
  LogOut, Menu, X, ChevronRight, ShieldCheck, Building, Calendar, CheckCircle, XCircle,
  BarChart3, Target, Sparkles,
} from "lucide-react";
import { InfluencerApprovalPanel } from "@/components/admin/InfluencerApprovalPanel";
import AffiliateApprovalPanel from "@/components/admin/AffiliateApprovalPanel";
import { AffiliateRebatesPanel } from "@/components/admin/AffiliateRebatesPanel";
import MediaOwnerManagementPanel from "@/components/admin/MediaOwnerManagementPanel";
import { CampaignBackfillTool } from "@/components/admin/CampaignBackfillTool";
import { CreativeMetadataBackfillTool } from "@/components/admin/CreativeMetadataBackfillTool";
import ActivityDismissalPanel from "@/components/admin/ActivityDismissalPanel";
import { AdminPurchaseOrderPanel } from "@/components/admin/AdminPurchaseOrderPanel";
import { AdminMakeAffiliatePanel } from "@/components/admin/AdminMakeAffiliatePanel";
import { CampaignsList } from "./AdminDashboard/components/CampaignsList";
import { AdminCampaign, SortOption } from "./AdminDashboard/types";

type TabKey = "campaigns" | "influencers" | "affiliates" | "rebates" | "media-owners" | "activity" | "purchase-orders" | "make-affiliate" | "backfill";

// ── Count-up hook ──
function useCountUp(target: number, duration = 1200, startDelay = 0) {
  const [count, setCount] = useState(0);
  const prevTarget = useRef(0);
  useEffect(() => {
    if (target === 0) { setCount(0); return; }
    const start = prevTarget.current;
    const diff = target - start;
    const timer = setTimeout(() => {
      const startTime = performance.now();
      const tick = (now: number) => {
        const elapsed = now - startTime;
        const progress = Math.min(elapsed / duration, 1);
        const ease = 1 - Math.pow(1 - progress, 3);
        setCount(Math.round(start + diff * ease));
        if (progress < 1) requestAnimationFrame(tick);
        else prevTarget.current = target;
      };
      requestAnimationFrame(tick);
    }, startDelay);
    return () => clearTimeout(timer);
  }, [target, duration, startDelay]);
  return count;
}

// ── Stat Card ──
interface StatCardProps {
  label: string; rawValue: number | string; countValue?: number;
  icon: React.ElementType; gradient: string; iconColor: string; delay: string;
}
const StatCard = ({ label, rawValue, countValue, icon: Icon, gradient, iconColor, delay }: StatCardProps) => {
  const displayValue = countValue !== undefined ? countValue.toLocaleString() : rawValue;
  return (
    <div className="relative rounded-2xl border border-border/60 bg-card overflow-hidden shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-300 animate-fade-in" style={{ animationDelay: delay }}>
      <div className={`absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r ${gradient}`} />
      <div className="absolute -right-3 -top-3 opacity-[0.06]"><Icon className={`h-20 w-20 ${iconColor}`} /></div>
      <div className="p-5 relative z-10">
        <div className={`p-2.5 rounded-xl bg-gradient-to-br ${gradient} inline-flex mb-3`}><Icon className="h-5 w-5 text-white" /></div>
        <p className="text-2xl font-bold text-foreground tabular-nums tracking-tight leading-none mb-1.5">{displayValue}</p>
        <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide">{label}</p>
        <div className={`mt-4 h-px bg-gradient-to-r ${gradient} opacity-20`} />
      </div>
    </div>
  );
};

const AdminDashboard = () => {
  const { language } = useLanguage();
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { isAdmin, checking } = useAdminCheck();
  const [campaigns, setCampaigns] = useState<AdminCampaign[]>([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState<string | null>(null);
  const [sortBy, setSortBy] = useState<SortOption>('created_desc');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [activeTab, setActiveTab] = useState<TabKey>("campaigns");
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const isRTL = language === 'ar';

  useEffect(() => { if (!checking && !isAdmin) navigate('/client-dashboard'); }, [isAdmin, checking, navigate]);

  // ── Fetch campaigns (unchanged logic) ──
  useEffect(() => {
    const fetchAllCampaigns = async () => {
      if (!user || !isAdmin || !isAuthenticated) return;
      try {
        setLoading(true);
        const { data: campaignsData, error } = await supabase.from('saved_campaigns').select('*').order('created_at', { ascending: false });
        if (error) { console.error('Error fetching campaigns:', error); toast({ title: "Error", description: isRTL ? 'فشل في جلب بيانات الحملات' : 'Failed to fetch campaigns data', variant: "destructive" }); return; }

        const transformedCampaigns = (campaignsData || []).map((campaign) => ({
          id: campaign.id,
          campaign_id: campaign.campaign_id,
          company_name: campaign.company_name || '',
          brand_name: campaign.brand_name || '',
          person_in_charge: campaign.person_in_charge || '',
          email: campaign.email || '',
          mobile: campaign.mobile || '',
          job_title: campaign.job_title || '',
          total_devices: campaign.devices_count || 0,
          duration_days: campaign.start_date && campaign.end_date ? Math.ceil((new Date(campaign.end_date).getTime() - new Date(campaign.start_date).getTime()) / (1000 * 60 * 60 * 24)) : 0,
          total_budget: campaign.total_budget || 0,
          start_date: campaign.start_date,
          end_date: campaign.end_date,
          status: campaign.status as 'pending' | 'approved' | 'rejected',
          created_at: campaign.created_at,
          updated_at: campaign.updated_at,
          selected_locations: Array.isArray(campaign.selected_locations) ? campaign.selected_locations : typeof campaign.selected_locations === 'string' ? JSON.parse(campaign.selected_locations) : campaign.selected_locations || [],
          payment_type: campaign.payment_type,
          rejection_reason: campaign.rejection_reason,
          creative_urls: campaign.creative_urls || [],
          creative_metadata: Array.isArray(campaign.creative_metadata) ? (campaign.creative_metadata as Array<{ url: string; locationName?: string; mediaOwner?: string; orientation?: 'portrait' | 'landscape'; scheduledDays?: number[]; resolution?: string; }>) : [],
          user_id: campaign.user_id,
          creator_email: campaign.creator_email,
          is_archived: campaign.is_archived,
          is_launched: campaign.is_launched,
          media_owner_name: campaign.media_owner_name,
          location_name: campaign.location_name,
          screen_types: campaign.screen_types,
          bank_transfer_status: campaign.bank_transfer_status,
          terms_accepted: campaign.terms_accepted,
          actual_reach: campaign.actual_reach,
          actual_engagement: campaign.actual_engagement,
          actual_spent: campaign.actual_spent,
        }));
        setCampaigns(transformedCampaigns);
      } catch (error) {
        console.error('Error in fetchAllCampaigns:', error);
        toast({ title: "Error", description: isRTL ? 'حدث خطأ أثناء جلب البيانات' : 'An error occurred while fetching data', variant: "destructive" });
      } finally {
        setLoading(false);
      }
    };
    if (isAuthenticated && user && isAdmin) fetchAllCampaigns();
  }, [user, language, toast, isAuthenticated, isAdmin]);

  // ── Update / Delete (unchanged) ──
  const updateCampaignStatus = async (campaignId: string, newStatus: 'approved' | 'rejected' | 'pending', rejectionReason?: string) => {
    setUpdating(campaignId);
    try {
      const campaign = campaigns.find(c => c.id === campaignId);
      const updateData: { status: string; rejection_reason?: string | null } = { status: newStatus };
      if (newStatus === 'rejected' && rejectionReason) updateData.rejection_reason = rejectionReason;
      else if (newStatus !== 'rejected') updateData.rejection_reason = null;

      const { error: updateError } = await supabase.from('saved_campaigns').update(updateData).eq('id', campaignId);
      if (updateError) throw updateError;

      if (campaign?.campaign_id) await supabase.from('campaigns').update({ status: newStatus }).eq('id', campaign.campaign_id);

      setCampaigns(prev => prev.map(c => c.id === campaignId ? { ...c, status: newStatus, rejection_reason: newStatus === 'rejected' ? rejectionReason : undefined } : c));

      if (newStatus === 'approved' || newStatus === 'rejected') {
        try {
          await supabase.functions.invoke('send-status-notification', { body: { campaignId, newStatus, campaignName: campaign?.brand_name || campaign?.company_name, recipientEmail: campaign?.email, rejectionReason } });
        } catch (emailError) { console.error('Error sending status notification:', emailError); }
      }

      const statusText = isRTL ? (newStatus === 'approved' ? 'الموافقة على' : newStatus === 'rejected' ? 'رفض' : 'إرجاع للمراجعة') : (newStatus === 'approved' ? 'approved' : newStatus === 'rejected' ? 'rejected' : 'returned to pending');
      toast({ title: "Success", description: isRTL ? `تم ${statusText} الحملة بنجاح` : `Campaign ${statusText} successfully` });
    } catch (error) {
      console.error('Error in updateCampaignStatus:', error);
      toast({ title: "Error", description: isRTL ? 'حدث خطأ أثناء التحديث' : 'An error occurred while updating', variant: "destructive" });
    } finally {
      setUpdating(null);
    }
  };

  const deleteCampaign = async (campaignId: string) => {
    setUpdating(campaignId);
    try {
      const campaign = campaigns.find(c => c.id === campaignId);
      await supabase.from('media_owner_campaign_approvals').delete().eq('campaign_id', campaignId);
      const { error } = await supabase.from('saved_campaigns').delete().eq('id', campaignId);
      if (error) throw error;
      if (campaign?.campaign_id) await supabase.from('campaigns').delete().eq('id', campaign.campaign_id);
      setCampaigns(prev => prev.filter(c => c.id !== campaignId));
      toast({ title: isRTL ? 'تم الحذف' : 'Deleted', description: isRTL ? 'تم حذف الحملة بنجاح' : 'Campaign deleted successfully' });
    } catch (error) {
      console.error('Error deleting campaign:', error);
      toast({ title: isRTL ? 'خطأ' : 'Error', description: isRTL ? 'فشل حذف الحملة' : 'Failed to delete campaign', variant: 'destructive' });
    } finally {
      setUpdating(null);
    }
  };

  const handleRefresh = () => { setLoading(true); window.location.reload(); };
  const handleSignOut = async () => { setIsAuthenticated(false); await signOut(); navigate('/'); };
  const handleAdminLogout = () => {
    setIsAuthenticated(false);
    toast({ title: isRTL ? 'تم تسجيل الخروج' : 'Logged Out', description: isRTL ? 'تم تسجيل الخروج من لوحة تحكم المدير' : 'You have been logged out of the admin dashboard' });
    navigate('/');
  };
  const authenticate = () => setIsAuthenticated(true);
  const switchTab = useCallback((tab: TabKey) => { setActiveTab(tab); setSidebarOpen(false); }, []);

  // Stats
  const totalCount = campaigns.length;
  const pendingCount = campaigns.filter(c => c.status === 'pending').length;
  const approvedCount = campaigns.filter(c => c.status === 'approved').length;
  const rejectedCount = campaigns.filter(c => c.status === 'rejected').length;

  const animTotal = useCountUp(totalCount, 1200, 0);
  const animPending = useCountUp(pendingCount, 1200, 80);
  const animApproved = useCountUp(approvedCount, 1200, 160);
  const animRejected = useCountUp(rejectedCount, 1200, 240);

  const navItems = useMemo(() => [
    { key: "campaigns" as TabKey, label: "Campaigns", labelAr: "الحملات", icon: Target },
    { key: "influencers" as TabKey, label: "Influencers", labelAr: "المؤثرين", icon: Users },
    { key: "affiliates" as TabKey, label: "Affiliates", labelAr: "الشركاء", icon: Handshake },
    { key: "rebates" as TabKey, label: "Rebates", labelAr: "المكافآت", icon: Sparkles },
    { key: "media-owners" as TabKey, label: "Media Owners", labelAr: "مالكي الوسائط", icon: Monitor },
    { key: "activity" as TabKey, label: "Activity", labelAr: "النشاط", icon: Bell },
    { key: "purchase-orders" as TabKey, label: "Purchase Orders", labelAr: "أوامر الشراء", icon: FileText },
    { key: "make-affiliate" as TabKey, label: "Manage Affiliates", labelAr: "إدارة الشركاء", icon: UserPlus },
    { key: "backfill" as TabKey, label: "Data Backfill", labelAr: "استعادة البيانات", icon: Database },
  ], []);

  // Early returns
  if (checking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-4">
          <div className="relative">
            <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary/20 border-t-primary" />
            <div className="absolute inset-0 rounded-full animate-ping border-2 border-primary/20" style={{ animationDuration: '1.5s' }} />
          </div>
          <p className="text-muted-foreground font-medium">{isRTL ? 'جاري التحقق...' : 'Verifying access...'}</p>
        </div>
      </div>
    );
  }

  if (!user || !isAdmin) return null;
  if (!isAuthenticated) return <AdminPasswordProtection onAuthenticated={authenticate} />;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-4">
          <div className="relative">
            <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary/20 border-t-primary" />
            <div className="absolute inset-0 rounded-full animate-ping border-2 border-primary/20" style={{ animationDuration: '1.5s' }} />
          </div>
          <p className="text-muted-foreground font-medium">{isRTL ? 'جاري التحميل...' : 'Loading...'}</p>
        </div>
      </div>
    );
  }

  const userInitials = (user.email?.[0] || 'A').toUpperCase();

  return (
    <div className={`min-h-screen bg-background ${isRTL ? 'text-right' : 'text-left'}`}>
      {/* ── HERO HEADER ── */}
      <div className="relative overflow-hidden border-b border-border/50"
        style={{ background: 'linear-gradient(135deg, hsl(var(--primary)/0.12) 0%, hsl(var(--primary)/0.04) 50%, transparent 100%)' }}>
        <div className="absolute -top-16 -right-16 w-48 h-48 rounded-full opacity-10" style={{ background: 'radial-gradient(circle, hsl(var(--primary)), transparent)' }} />
        <div className="absolute -bottom-8 -left-8 w-32 h-32 rounded-full opacity-5" style={{ background: 'hsl(var(--primary))' }} />

        <div className="container mx-auto px-4 sm:px-6 py-6 relative z-10">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <button className="lg:hidden p-2 rounded-xl bg-card border border-border shadow-sm hover:bg-accent transition-colors" onClick={() => setSidebarOpen(true)}>
                <Menu className="h-5 w-5 text-foreground" />
              </button>
              <div className="p-3 bg-primary/10 rounded-2xl border border-primary/20">
                <ShieldCheck className="h-8 w-8 text-primary" />
              </div>
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold text-foreground leading-tight tracking-tight">{isRTL ? 'لوحة تحكم المدير' : 'Admin Dashboard'}</h1>
                <p className="text-sm text-muted-foreground">{isRTL ? 'إدارة جميع الحملات الإعلانية' : 'Manage all advertising campaigns'}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-full bg-card border border-border shadow-sm">
                <div className="w-6 h-6 rounded-full bg-primary/20 flex items-center justify-center text-xs font-bold text-primary">{userInitials}</div>
                <span className="text-xs font-medium text-foreground">{isRTL ? 'مدير' : 'Admin'}</span>
              </div>
              <Button variant="outline" size="sm" onClick={() => navigate('/admin/performance')} className="rounded-xl border-border/70">
                <BarChart3 className="h-4 w-4 mr-1.5" /><span className="hidden sm:inline">{isRTL ? 'الأداء' : 'Performance'}</span>
              </Button>
              <Button variant="outline" size="sm" onClick={handleRefresh} className="rounded-xl border-border/70">
                <RefreshCw className="h-4 w-4 mr-1.5" /><span className="hidden sm:inline">{isRTL ? 'تحديث' : 'Refresh'}</span>
              </Button>
              <Button variant="outline" size="sm" onClick={handleAdminLogout} className="rounded-xl border-border/70 hover:border-destructive/50 hover:text-destructive transition-all">
                <LogOut className="h-4 w-4 mr-1.5" /><span className="hidden sm:inline">{isRTL ? 'خروج المدير' : 'Admin Logout'}</span>
              </Button>
              <Button variant="destructive" size="sm" onClick={handleSignOut} className="rounded-xl">
                <span>{isRTL ? 'تسجيل الخروج' : 'Sign Out'}</span>
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* ── STATS ── */}
      <div className="container mx-auto px-4 sm:px-6 py-5">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard label={isRTL ? 'إجمالي الحملات' : 'Total Campaigns'} rawValue={totalCount} countValue={animTotal} icon={Building} gradient="from-blue-500 to-blue-600" iconColor="text-blue-500" delay="0ms" />
          <StatCard label={isRTL ? 'في انتظار المراجعة' : 'Pending Review'} rawValue={pendingCount} countValue={animPending} icon={Calendar} gradient="from-amber-500 to-orange-500" iconColor="text-amber-500" delay="80ms" />
          <StatCard label={isRTL ? 'موافق عليها' : 'Approved'} rawValue={approvedCount} countValue={animApproved} icon={CheckCircle} gradient="from-emerald-500 to-emerald-600" iconColor="text-emerald-500" delay="160ms" />
          <StatCard label={isRTL ? 'مرفوضة' : 'Rejected'} rawValue={rejectedCount} countValue={animRejected} icon={XCircle} gradient="from-red-500 to-red-600" iconColor="text-red-500" delay="240ms" />
        </div>
      </div>

      {/* ── LAYOUT: Sidebar + Content ── */}
      <div className="container mx-auto px-4 sm:px-6 pb-10">
        <div className="flex gap-6 items-start">
          {/* Desktop Sidebar */}
          <nav className="hidden lg:flex flex-col w-56 flex-shrink-0 sticky top-6">
            <div className="rounded-2xl border border-border/60 bg-card shadow-sm overflow-hidden">
              <div className="p-2 space-y-0.5">
                {navItems.map((item) => {
                  const Icon = item.icon;
                  const isActive = activeTab === item.key;
                  return (
                    <button key={item.key} onClick={() => switchTab(item.key)}
                      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 group relative
                        ${isActive ? 'bg-primary text-primary-foreground shadow-sm' : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'}`}>
                      {isActive && <div className="absolute left-0 top-1/2 -translate-y-1/2 w-0.5 h-5 bg-primary-foreground/60 rounded-r-full" />}
                      <Icon className={`h-4 w-4 flex-shrink-0 ${isActive ? 'text-primary-foreground' : 'text-muted-foreground group-hover:text-accent-foreground'}`} />
                      <span className="flex-1 text-left">{isRTL ? item.labelAr : item.label}</span>
                      {isActive && <ChevronRight className="h-3.5 w-3.5 text-primary-foreground/60" />}
                    </button>
                  );
                })}
              </div>
              <div className="border-t border-border/50 p-2 mt-1">
                <button onClick={handleAdminLogout} className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-muted-foreground hover:bg-destructive/10 hover:text-destructive transition-all">
                  <LogOut className="h-4 w-4" /><span>{isRTL ? 'تسجيل الخروج' : 'Sign Out'}</span>
                </button>
              </div>
            </div>
          </nav>

          {/* Mobile Sidebar Overlay */}
          {sidebarOpen && (
            <div className="lg:hidden fixed inset-0 z-50 flex">
              <div className="absolute inset-0 bg-background/80 backdrop-blur-sm" onClick={() => setSidebarOpen(false)} />
              <div className="relative w-64 bg-card border-r border-border shadow-2xl flex flex-col">
                <div className="flex items-center justify-between p-4 border-b border-border">
                  <span className="font-semibold text-foreground">{isRTL ? 'القائمة' : 'Navigation'}</span>
                  <button onClick={() => setSidebarOpen(false)} className="p-1.5 rounded-lg hover:bg-accent"><X className="h-4 w-4" /></button>
                </div>
                <nav className="flex-1 p-2 space-y-0.5 overflow-y-auto">
                  {navItems.map((item) => {
                    const Icon = item.icon;
                    const isActive = activeTab === item.key;
                    return (
                      <button key={item.key} onClick={() => switchTab(item.key)}
                        className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all
                          ${isActive ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'}`}>
                        <Icon className="h-4 w-4 flex-shrink-0" />
                        <span className="flex-1 text-left">{isRTL ? item.labelAr : item.label}</span>
                      </button>
                    );
                  })}
                </nav>
                <div className="border-t border-border p-2">
                  <button onClick={handleAdminLogout} className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-muted-foreground hover:bg-destructive/10 hover:text-destructive transition-all">
                    <LogOut className="h-4 w-4" /><span>{isRTL ? 'تسجيل الخروج' : 'Sign Out'}</span>
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Content Area */}
          <main className="flex-1 min-w-0 animate-fade-in" key={activeTab}>
            {activeTab === 'campaigns' && <CampaignsList campaigns={campaigns} sortBy={sortBy} setSortBy={setSortBy} updating={updating} onUpdateStatus={updateCampaignStatus} onDelete={deleteCampaign} />}
            {activeTab === 'influencers' && <InfluencerApprovalPanel />}
            {activeTab === 'affiliates' && <AffiliateApprovalPanel />}
            {activeTab === 'rebates' && <AffiliateRebatesPanel />}
            {activeTab === 'media-owners' && <MediaOwnerManagementPanel />}
            {activeTab === 'activity' && <ActivityDismissalPanel />}
            {activeTab === 'purchase-orders' && <AdminPurchaseOrderPanel />}
            {activeTab === 'make-affiliate' && <AdminMakeAffiliatePanel />}
            {activeTab === 'backfill' && <div className="space-y-6"><CreativeMetadataBackfillTool /><CampaignBackfillTool /></div>}
          </main>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
